/**
 * Estrutura de blocos da Batalha Infinita.
 *
 * A onda deixa de ser um número solto e passa a ocupar uma posição num bloco
 * de 10 com forma reconhecível:
 *
 *   1  2  3  4     5        6  7  8  9      10
 *   └─ normais ─┘  ELITE    └── normais ──┘  CHEFE
 *
 * Saber que a onda 10 é um chefe — e ver isso anunciado desde a onda 7 — é o
 * que dá horizonte à corrida. Sem horizonte, "infinito" é só repetição.
 */

export type WaveKind = "normal" | "elite" | "boss";

export const WAVES_PER_BLOCK = 10;
export const ELITE_SLOT = 5;

export function getWaveKind(wave: number): WaveKind {
  if (wave <= 0) return "normal";
  const slot = ((wave - 1) % WAVES_PER_BLOCK) + 1;
  if (slot === WAVES_PER_BLOCK) return "boss";
  if (slot === ELITE_SLOT) return "elite";
  return "normal";
}

export function isBossWave(wave: number): boolean {
  return getWaveKind(wave) === "boss";
}

export function isEliteWave(wave: number): boolean {
  return getWaveKind(wave) === "elite";
}

/** Índice do bloco (1 para as ondas 1-10, 2 para 11-20, ...). */
export function getBlockIndex(wave: number): number {
  return Math.floor((Math.max(1, wave) - 1) / WAVES_PER_BLOCK) + 1;
}

/** Próxima onda de chefe a partir da onda atual (inclusive). */
export function nextBossWave(wave: number): number {
  return getBlockIndex(wave) * WAVES_PER_BLOCK;
}

/**
 * O aviso de chefe acende 3 ondas antes. Antes disso ele seria ruído; depois,
 * tarde demais para o jogador guardar recursos.
 */
export const BOSS_WARNING_LEAD = 3;

export function shouldWarnAboutBoss(wave: number): boolean {
  const boss = nextBossWave(wave);
  return boss - wave <= BOSS_WARNING_LEAD && boss > wave;
}

// ============================================
// MODIFICADORES DE MUNDO
// ============================================

/**
 * A partir do segundo bloco, cada bloco carrega um modificador que muda a cara
 * da corrida. É assim que a dificuldade sobe depois da onda 20 sem transformar
 * inimigos em esponjas de dano: muda o COMO, não o quanto.
 */
export type WorldModifier =
  | "none"
  | "swarm"        // mais inimigos, todos mais frágeis
  | "juggernaut"   // menos inimigos, muito mais resistentes
  | "sharpshooter" // atiradores em toda parte
  | "blitz"        // todo mundo mais rápido
  | "volatile";    // inimigos explodem ao morrer

export const WORLD_MODIFIERS: readonly WorldModifier[] = Object.freeze([
  "swarm",
  "juggernaut",
  "sharpshooter",
  "blitz",
  "volatile",
]);

export const WORLD_MODIFIER_ICON: Record<WorldModifier, string> = {
  none: "",
  swarm: "🐝",
  juggernaut: "🗿",
  sharpshooter: "🎯",
  blitz: "⚡",
  volatile: "💣",
};

/**
 * Modificador do bloco. O primeiro bloco nunca tem — o jogador precisa de dez
 * ondas para aprender o jogo base antes de o jogo começar a mudar as regras.
 */
export function getWorldModifier(wave: number, runSeed: number): WorldModifier {
  const block = getBlockIndex(wave);
  if (block <= 1) return "none";
  // Rotação embaralhada pela semente da corrida: dois jogadores na mesma onda
  // não enfrentam necessariamente o mesmo modificador.
  const offset = runSeed % WORLD_MODIFIERS.length;
  return WORLD_MODIFIERS[(block - 2 + offset) % WORLD_MODIFIERS.length];
}

// ============================================
// MEDALHAS
// ============================================

export interface Medal {
  id: string;
  wave: number;
  icon: string;
}

export const MEDALS: readonly Medal[] = Object.freeze([
  { id: "bronze", wave: 10, icon: "🥉" },
  { id: "silver", wave: 20, icon: "🥈" },
  { id: "gold", wave: 30, icon: "🥇" },
  { id: "diamond", wave: 50, icon: "💎" },
]);

export function medalsFor(bestWave: number): Medal[] {
  return MEDALS.filter((medal) => bestWave >= medal.wave);
}

/** Próxima medalha ainda não conquistada, ou null se todas já foram. */
export function nextMedal(bestWave: number): Medal | null {
  return MEDALS.find((medal) => bestWave < medal.wave) ?? null;
}
